// ─────────────────────────────────────────────────────────────────────────────
// Scoring atoms — pure functions, no side-effects.
// Score is always one of: "X", "10".."1", "M" (miss).
// ─────────────────────────────────────────────────────────────────────────────

import {
  CENTER,
  WA_OUTER_R,
  VEGAS_SPOT_CENTERS,
  VEGAS_SPOT_R,
  TRI_SPOT_CENTERS,
  TRI_SPOT_R,
  NFAA5_SPOT_CENTERS,
  NFAA5_SPOT_R,
  NFAA_SINGLE_OUTER_R,
  WAFIELD3_SPOT_CENTERS,
  WAFIELD3_SPOT_R,
  D3_VITAL,
  D3_OUTER_R,
  toVbX,
  toVbY,
} from "./targetGeometry";
import faceDefinitions from "../../../shared/face-definitions.json";

const { faceTypes: _FACE_TYPES, faces: _FACES } = faceDefinitions;

// Faces the app exposes for direct/custom face selection — a curated subset of
// shared/face-definitions.json `faces`. wa60/wa80/nfaaoutdoor exist in the
// shared registry (consumed by catalog round-type face resolution) but are not
// offered as a selectable custom face, so they're intentionally excluded from
// every per-face table below (#1680 PR3). Mirrors the pre-#1680 hardcoded face
// tables' key sets exactly.
const SUPPORTED_FACES = [
  "wa40", "wa122", "vegas", "3spot-tri", "nfaa5spot", "nfaaspot", "nfaafield",
  "nfaahunter", "3d", "ibo3d", "3d-2arrow", "imperial5zone", "wa5zone",
  "wa6ring", "wa5ring80", "wa5ring122", "worcester",
  "wafield40", "wafield60", "wafield80", "wafield3spot",
];

/** The faceType row (radius_map, chip colours, keypad keys, …) for `face`, or
 *  null when `face` isn't in the shared registry. */
function faceTypeFor(face) {
  const f = _FACES[face];
  return f ? _FACE_TYPES[f.face_type] : null;
}

/** CSS class (matches .chip.{class}) for a score value. */
export function scoreClass(s) {
  if (s === "X" || s === "12" || s === "11") return "x";
  if (s === 10 || s === 9 || s === "10" || s === "9") return "gold";
  if (s === 8  || s === 7  || s === "8"  || s === "7")  return "cinder";
  if (s === 6  || s === 5  || s === "6"  || s === "5")  return "field";
  if (s === 4  || s === 3  || s === "4"  || s === "3")  return "nock";
  if (s === 2  || s === 1  || s === "2"  || s === "1")  return "vellum";
  return "miss";
}

// Per-face chip colour map — zone string -> design token. Rings on NFAA and 3D
// faces don't follow the WA colour-to-value convention: white centre = X/5 on
// NFAA; white inner vital = 12/11 on 3D. Sourced from shared/face-definitions.json
// (#1680 PR3) — every SUPPORTED_FACES entry gets its faceType's chip_zone_colors
// map (verified to reproduce the pre-migration hand-written override table
// exactly, including the faces that used to fall through to `scoreClass()`).
const _FACE_CHIP_CLASSES = {};
for (const face of SUPPORTED_FACES) {
  const ft = faceTypeFor(face);
  if (ft) _FACE_CHIP_CLASSES[face] = ft.chip_zone_colors;
}

/** Face-aware chip class. Falls back to `scoreClass` for faces not listed. */
export function scoreClassForFace(s, face) {
  const map = _FACE_CHIP_CLASSES[face];
  if (map) {
    const key = s === "M" ? "M" : String(s);
    if (key in map) return map[key];
  }
  return scoreClass(s);
}

// CSS-var colour that matches the physical ring a score lands in.
// Used for SVG fills (arrow dots, end bars, heatmap overlays).
// Kept separate from the chip class system — the chip "x" class adds italic
// styling on top of gold; ring-colour only needs the fill.
// Note: WA 4/3 uses --ring-dark (soft black #2D2D2D) not --nock (pure black
// #0A0A0A) so dot colours on the black rings aren't overly harsh.
const _CLASS_TO_CSS_VAR = {
  x:      "var(--gold)",
  gold:   "var(--gold)",
  cinder: "var(--cinder)",
  field:  "var(--field)",
  nock:   "var(--ring-dark)",
  vellum: "var(--vellum)",
  miss:   "var(--miss)",
};

/**
 * Face-aware ring colour for a score — returns a CSS var string suitable for
 * SVG `fill` attributes.  Single source of truth for all visualisations that
 * colour arrow dots by the ring they landed in.
 */
export function scoreRingColor(score, face) {
  const cls = scoreClassForFace(score, face);
  return _CLASS_TO_CSS_VAR[cls] ?? "var(--miss)";
}

// Maximum point value a single arrow can score on each face — the face's top
// ring value (JSON `max_arrow`, #1680 PR3). Mirrors backend `_MAX_ARROW_SCORE`
// in routers/archers.py. Used to series the Score Trend by scoring system
// (10-zone WA vs 5-zone NFAA) so avg arrow scores are only compared within a
// comparable scale. WA 5-zone tops out at 9 (gold); WA 6-ring / WA 5-ring are
// full 10-ring faces truncated at ring 5/6, so their top arrow value is still
// 10 (X = 10). 3D faces: ASA inner-vital scores 12; the IBO faces' inner-vital
// scores 11 — there is no 12 ring on ibo3d / 3d-2arrow (#1667). WA Field faces:
// max arrow = 6 (X counts as 6 for totals).
const _MAX_ARROW_SCORE = {};
for (const face of SUPPORTED_FACES) {
  const ft = faceTypeFor(face);
  if (ft) _MAX_ARROW_SCORE[face] = ft.max_arrow;
}

/** Top ring value (max points per arrow) for `face`; defaults to 10. */
export function maxArrowScoreForFace(face) {
  return _MAX_ARROW_SCORE[face] ?? 10;
}

// LEGAL_SCORES — every score string that may legally be PERSISTED for a face,
// from shared/face-definitions.json `legal_scores` (#2023). Mirrors backend
// `legal_scores_for_face` (app/scoring.py). Distinct from FACE_KEYS
// (`keypad_keys`, below), which is the tap-entry subset: a Vegas triple face
// only offers 10..6 on the keypad but a 5..1 read off a paper card is still a
// legal score for it. Consumers validating a score that did NOT come from the
// keypad — OCR scorecard review, pasted/typed cells — must use this, not
// FACE_KEYS and never a hand-written set.
const _LEGAL_SCORES = {};

// Fallback for a null/unknown face: the plain 10-ring set, still read off the
// shared JSON rather than re-typed here.
const _DEFAULT_LEGAL_SCORES = new Set(_FACE_TYPES.wa_full.legal_scores);

// Faces whose legal scores are NOT the plain ten-zone run, and so must override
// the zone-count fallback in `score()` (#2041). Derived here rather than
// hand-listed: a layout needs an override exactly when its `legal_scores`
// differ from the plain run. Mirrors backend `_LEGAL_SCORES_BY_FACE` /
// `_NO_LEGAL_OVERRIDE` (app/scoring.py), which makes the same split.
const _PLAIN_RUN = [..._DEFAULT_LEGAL_SCORES].sort().join(",");
const _LEGAL_OVERRIDE_FACES = new Set();

for (const face of SUPPORTED_FACES) {
  const ft = faceTypeFor(face);
  if (!ft) continue;
  _LEGAL_SCORES[face] = new Set(ft.legal_scores);
  if ([...ft.legal_scores].sort().join(",") !== _PLAIN_RUN) {
    _LEGAL_OVERRIDE_FACES.add(face);
  }
}

/**
 * Set of legal score strings for `face` (e.g. {"X","5","4","M"} for
 * nfaa5spot, {"12","10","8","5","M"} for ASA 3D). Defaults to the 10-ring WA
 * set when `face` is null or not in the shared registry.
 */
export function legalScoresForFace(face) {
  return _LEGAL_SCORES[face] ?? _DEFAULT_LEGAL_SCORES;
}

/** True when `face` declares a legal-score set other than the plain ten-zone
 *  run — i.e. `legalScoresForFace(face)` must win over the zone-count run. */
export function faceOverridesLegalScores(face) {
  return _LEGAL_OVERRIDE_FACES.has(face);
}

/** Label as shown on a chip — "X", "1".."10", or "M". */
export function scoreLabel(s) {
  if (s === "X") return "X";
  if (s === 0 || s == null || s === "M") return "M";
  return String(s);
}

/**
 * Numeric value for totalling. Pass `scoringZones` (default 10) so X counts
 * as the correct maximum for the active face — 5 for NFAA faces, 10 for WA.
 */
export function scoreValue(s, scoringZones = 10) {
  if (s === "X") return scoringZones;
  if (s === "M" || s == null) return 0;
  return Number(s);
}

export const sumEnd   = (arr, scoringZones = 10) => arr.reduce((t, s) => t + scoreValue(s, scoringZones), 0);
export const countX   = (arr) => arr.filter((s) => s === "X").length;

// ─────────────────────────────────────────────────────────────────────────────
// Inner-ten (compound) rule — #1868, made face-conditional in #2172. On an
// inner_ten round only the X-ring scores 10: the outer 10-ring scores 9, so a
// raw "10" is never a legal persisted score. This is an ENGINE rule (round flag
// `round.inner_ten`), not a UI-hook concern — useScoreEntry delegates here.
// Mirrors apply_inner_ten / inner_ten_violations in backend/app/scoring.py; the
// shared fixture's innerTen cases pin both sides.
//
// The rule names three specific rings ("the X-ring scores 10, the outer 10-ring
// scores 9"), so it only applies to a face that has all three. It used to be
// applied face-blind, and `round.inner_ten` is set from bow style with no
// round-type gate, so every compound archer on a 3D round hit it: this module
// rewrote their 10-ring hit to "9" — a value an ASA/IBO animal cannot hold — and
// the backend 422'd it. Where the rings are absent the rule is now inert.
// ─────────────────────────────────────────────────────────────────────────────

const _INNER_TEN_RINGS = ["X", "10", "9"];

/**
 * Whether the inner-ten (compound) rule is meaningful on `face` — true exactly
 * when the face's legal scores contain all three rings the rule names. Reads
 * the face SSOT (`legalScoresForFace`, from shared/face-definitions.json
 * `legal_scores`) and NOT `keypad_keys`, which is only the tap-entry subset.
 * A null or unregistered face falls back to the plain WA ten-ring set, so
 * custom rounds and the legacy target_face codes that predate #2115 behave as
 * they always have. Mirrors backend `face_supports_inner_ten`.
 */
export function faceSupportsInnerTen(face = null) {
  const legal = legalScoresForFace(face);
  return _INNER_TEN_RINGS.every((ring) => legal.has(ring));
}

export const INNER_TEN_VIOLATION =
  "score '10' is invalid for compound (inner_ten) rounds; use 'X' or '9'";

/**
 * The violation message with its ADVICE derived from `face` rather than
 * hardcoded, so it can never name a ring the face does not have (#2172).
 * Only reached behind `faceSupportsInnerTen`, which requires both "X" and "9"
 * — so wherever the rule still fires this equals INNER_TEN_VIOLATION exactly
 * and the wire contract is unchanged. Mirrors backend
 * `inner_ten_violation_message`.
 */
export function innerTenViolationMessage(face = null) {
  const legal = legalScoresForFace(face);
  const alts = ["X", "9"].filter((s) => legal.has(s));
  const base = "score '10' is invalid for compound (inner_ten) rounds";
  if (alts.length === 0) return base;
  return `${base}; use ${alts.map((a) => `'${a}'`).join(" or ")}`;
}

/**
 * The inner-ten 10→9 conversion: an outer-10 becomes "9"; anything else
 * (including "X") passes through. hitZone() can return the number 10 and
 * keypad values are also numeric, so a raw 10 is matched via String() — a
 * converted score is always the string "9". No-op when `innerTen` is falsy,
 * and no-op on a face with no inner-ten rings (#2172).
 */
export function applyInnerTen(score, innerTen = true, face = null) {
  return innerTen && String(score) === "10" && faceSupportsInnerTen(face)
    ? "9"
    : score;
}

/** One violation per raw "10" in `arrows`, in arrow order — empty when the
 *  rule does not apply to `face` (#2172). */
export function innerTenViolations(arrows, face = null) {
  if (!faceSupportsInnerTen(face)) return [];
  const message = innerTenViolationMessage(face);
  return arrows.filter((a) => String(a) === "10").map(() => message);
}

/**
 * X count honoring `xDefinition`. In a "ten_only" (compound) round any 10 is
 * counted as an X too; otherwise only a literal "X" counts. Mirrors the xCount
 * branch of `score()` so the in-progress badge matches what Confirm persists. A
 * 10 may arrive as the number `10` or the string `"10"` depending on the entry
 * path, so both are accepted.
 */
export const countXForRound = (arr, xDefinition = "inner10") =>
  xDefinition === "ten_only"
    ? arr.filter((s) => s === "X" || s === 10 || s === "10").length
    : countX(arr);

// ─────────────────────────────────────────────────────────────────────────────
// Display-order sort (#1473) — scorecards show each end's arrows highest→lowest
// by scoring RANK, not raw value: X ranks above any digit (even though X is
// worth 10/5/6 in points), digits descend numerically, and M (miss) is last.
// This is DISPLAY-ONLY: it never reorders stored Arrow rows (arrow_index stays
// the shot/identity order for grouping & scatter) — callers sort a copy of the
// rendered score list. Mirrored by `arrow_display_rank` in
// backend/app/scorecard/base.py so the PDF card sorts identically.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rank a score for descending display order. Higher rank renders first.
 *   X            → top rank (above any digit)
 *   "12".."1"    → its integer value
 *   M / null / "" → below every scoring value (last)
 * Tie order among equal ranks is the caller's array order (stable sort).
 */
export function arrowDisplayRank(s) {
  if (s === "X") return Number.POSITIVE_INFINITY;
  if (s === "M" || s == null || s === "") return Number.NEGATIVE_INFINITY;
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

/**
 * Return a NEW array of `arrows` sorted by descending scoring rank
 * (X > 10 > 9 > … > 1 > M). `null`/empty placeholders sort last, so an
 * in-progress live end keeps its trailing empty slots at the end. The input
 * array is never mutated.
 */
export function sortArrowsDescending(arrows) {
  return arrows
    .map((s, i) => [s, i])
    .sort((a, b) => {
      const d = arrowDisplayRank(b[0]) - arrowDisplayRank(a[0]);
      // NaN guard (−Infinity − −Infinity): fall back to stable input order.
      return Number.isNaN(d) ? a[1] - b[1] : d || a[1] - b[1];
    })
    .map(([s]) => s);
}

/**
 * Same descending display order as sortArrowsDescending, but for Arrow
 * OBJECTS (`{ score, arrow_index, … }`) rather than bare score strings
 * (#1473). Ties break on `arrow_index`, so equal scores keep shot order.
 * Returns a NEW array — the stored arrow_index (shot/identity order, used for
 * grouping & scatter) is untouched.
 *
 * `arrow_index` is coalesced to 0 when absent: a missing index would make the
 * tie-break NaN, and a comparator that returns NaN leaves the sort in an
 * implementation-defined order. Bale-scorecard peer rows can arrive without
 * one, which is why the BaleScorecard copy of this had the guard and the
 * RoundDetailView copy — otherwise byte-identical, comment included — did not
 * (#2068).
 */
export function sortArrowObjectsDescending(arrows) {
  return [...arrows].sort(
    (a, b) =>
      arrowDisplayRank(b.score) - arrowDisplayRank(a.score) ||
      (a.arrow_index ?? 0) - (b.arrow_index ?? 0),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Radius → score — zoneForRadiusFromTable, table-driven (#1680 PR3).
//
// The hand-written per-face radiusToScore*() if/else chains that used to live
// here (radiusToScore, radiusToScoreSpot, radiusToScoreNFAA5Spot, …) are gone
// — hitZone (below) and zoneForRadius now both resolve through this single
// lookup, which reads `radius_map` straight from shared/face-definitions.json.
// `radius_map` is an ASCENDING list of `[max_radius, zone]` pairs; a radius
// resolves to the FIRST entry whose max_radius is >= it — the line-cutter
// rule: a boundary radius scores the inner/earlier zone. A radius past the
// last entry (or a face absent from the shared registry) scores "M" / null
// respectively. A (temporary, since-removed) parity test proved 100%
// agreement between the old per-face functions and this table-driven lookup
// across every case in shared/scoring-fixtures.json before the swap was made.
// ─────────────────────────────────────────────────────────────────────────────

/** Resolve `radius` against an ascending `[max_radius, zone]` pair list. */
function _zoneFromRadiusMap(radiusMap, radius) {
  for (const [maxRadius, zone] of radiusMap) {
    if (radius <= maxRadius) return zone;
  }
  return "M";
}

/**
 * The scoring zone (a canonical zone STRING, e.g. "X" / "10" / "M") an arrow at
 * `radius` lands on `face`, resolved from shared/face-definitions.json. Returns
 * `null` when `face` isn't in the shared registry (so callers can choose their
 * own unknown-face fallback — hitZone's default falls back to WA scoring,
 * zoneForRadius falls back to "M").
 */
export function zoneForRadiusFromTable(face, radius) {
  const ft = faceTypeFor(face);
  if (!ft) return null;
  return _zoneFromRadiusMap(ft.radius_map, radius);
}

// ─────────────────────────────────────────────────────────────────────────────
// hitZone — pure coordinate-to-score resolver
//
// Accepts SVG viewbox coordinates (0–240, centre at 120,120) and a face
// identifier string. Returns the score string for that point.
//
// This is the single source of truth for all hit-detection logic; the
// Target component delegates all scoring decisions here.
//
// Supported face ids:
//   "wa40"      WA 10-ring (default, 40 cm indoor)
//   "wa122"     WA 10-ring (same scoring geometry, 122 cm outdoor)
//   "vegas"     WA triple-spot inner face
//   "nfaa5spot" NFAA 5-spot indoor
//   "nfaafield" NFAA Field face
//   "3spot-tri" WA triangular triple-spot (two spots low, one high)
//   "3d"        3D animal — concentric vital rings (12/10/8/5)
// ─────────────────────────────────────────────────────────────────────────────

// ─── Target face classification ──────────────────────────────────────────────
// Every supported face, classified by spot layout. A "single" face is one
// scoring face; a "multi" face has several separate spots and is subject to
// the multi-spot lowest-arrow rule. This is the single source of truth — the
// multi-spot faces are exactly the keys of SPOT_CENTERS_BY_FACE. Mirrored by
// _FACE_KINDS in backend/app/scoring.py.
// ─────────────────────────────────────────────────────────────────────────────
// NFAA Field Round — 14-target unit definition
//
// One round = 28 ends (two passes through the same 14 targets). Each end = 4
// arrows. The first 10 targets are "unit" shots (all arrows from the same
// stake); targets 11–14 are "four-position" shots where each arrow comes from
// a different stake/position.
//
// Distances for targets 1–13 are in yards; target 14 is in feet.
// ─────────────────────────────────────────────────────────────────────────────

export const NFAA_FIELD_TARGETS = [
  { target:  1, face_cm:  35, arrows: [15, 15, 15, 15], unit: "yd" },
  { target:  2, face_cm:  35, arrows: [20, 20, 20, 20], unit: "yd" },
  { target:  3, face_cm:  35, arrows: [25, 25, 25, 25], unit: "yd" },
  { target:  4, face_cm:  35, arrows: [30, 30, 30, 30], unit: "yd" },
  { target:  5, face_cm:  50, arrows: [40, 40, 40, 40], unit: "yd" },
  { target:  6, face_cm:  50, arrows: [45, 45, 45, 45], unit: "yd" },
  { target:  7, face_cm:  50, arrows: [50, 50, 50, 50], unit: "yd" },
  { target:  8, face_cm:  65, arrows: [55, 55, 55, 55], unit: "yd" },
  { target:  9, face_cm:  65, arrows: [60, 60, 60, 60], unit: "yd" },
  { target: 10, face_cm:  65, arrows: [65, 65, 65, 65], unit: "yd" },
  // Four-position targets — each arrow from a different stake or position
  { target: 11, face_cm:  50, arrows: [35, 35, 35, 35], unit: "yd", position: true },
  { target: 12, face_cm:  50, arrows: [45, 40, 35, 30], unit: "yd", position: true },
  { target: 13, face_cm:  65, arrows: [80, 70, 60, 50], unit: "yd", position: true },
  { target: 14, face_cm:  20, arrows: [35, 30, 25, 20], unit: "ft", position: true },
];

/**
 * The field target for a given end number (1-based). Wraps at 14 so the
 * second pass (ends 15–28) revisits targets 1–14 in the same order.
 */
export function fieldTargetForEnd(endNumber) {
  return NFAA_FIELD_TARGETS[((endNumber - 1) % 14)];
}

// ─────────────────────────────────────────────────────────────────────────────
// NFAA Championship Hunter Round — 14-target unit
//
// Targets 1–4: walk-up (4 different distances per target).
// Targets 5–7: unit shots (4 arrows, same stake).
// Targets 8–10: position shots (same distance, 4 different positions).
// Targets 11–13: 2 arrows at each of 2 distances (e.g. 23/23/20/20 yd).
// Target 14: 20 cm face shot vertically (4 arrows at 11 yd).
// ─────────────────────────────────────────────────────────────────────────────

export const NFAA_HUNTER_TARGETS = [
  { target:  1, face_cm:  65, arrows: [70, 65, 61, 58], unit: "yd", position: true },
  { target:  2, face_cm:  65, arrows: [64, 59, 55, 52], unit: "yd", position: true },
  { target:  3, face_cm:  65, arrows: [58, 53, 48, 45], unit: "yd", position: true },
  { target:  4, face_cm:  50, arrows: [53, 48, 44, 41], unit: "yd", position: true },
  { target:  5, face_cm:  50, arrows: [48, 48, 48, 48], unit: "yd" },
  { target:  6, face_cm:  50, arrows: [44, 44, 44, 44], unit: "yd" },
  { target:  7, face_cm:  50, arrows: [40, 40, 40, 40], unit: "yd" },
  { target:  8, face_cm:  50, arrows: [36, 36, 36, 36], unit: "yd", position: true },
  { target:  9, face_cm:  35, arrows: [32, 32, 32, 32], unit: "yd", position: true },
  { target: 10, face_cm:  35, arrows: [28, 28, 28, 28], unit: "yd", position: true },
  { target: 11, face_cm:  35, arrows: [23, 23, 20, 20], unit: "yd", position: true },
  { target: 12, face_cm:  35, arrows: [19, 19, 17, 17], unit: "yd", position: true },
  { target: 13, face_cm:  35, arrows: [15, 15, 14, 14], unit: "yd", position: true },
  // 20 cm target shot vertically (bottom-to-top or top-to-bottom order)
  { target: 14, face_cm:  20, arrows: [11, 11, 11, 11], unit: "yd" },
];

/** The hunter target for a given end number (1-based). Wraps at 14. */
export function hunterTargetForEnd(endNumber) {
  return NFAA_HUNTER_TARGETS[((endNumber - 1) % 14)];
}

// ─────────────────────────────────────────────────────────────────────────────
// FACE_KEYS — valid tap-entry scores for each face, high-to-low with M last.
// Used by the Keypad to show only keys the archer can legally enter. Sourced
// from shared/face-definitions.json `keypad_keys` (#1680 PR3) — every entry is
// now a canonical STRING (e.g. "10", not the bare number 10 some hand-written
// per-face tables used inconsistently); every downstream consumer already
// tolerates both forms (String()/Number() coercion — see useScoreEntry.js and
// scoring.js's own scoreClass/scoreValue/zoneValue), and the wire schema
// (`ScoreStr`, backend/app/schemas/common.py) is itself a string Literal.
//
// FACE_KINDS — "single" vs "multi" spot classification, from JSON `kind`.
// ─────────────────────────────────────────────────────────────────────────────

export const FACE_KEYS = {};
export const FACE_KINDS = {};
for (const face of SUPPORTED_FACES) {
  const ft = faceTypeFor(face);
  if (!ft) continue;
  FACE_KEYS[face] = ft.keypad_keys;
  FACE_KINDS[face] = ft.kind;
}

/** True when `face` is a multi-spot face — several separate spots, each
 *  scored on its own and subject to the multi-spot lowest-arrow rule. */
export function isMultiSpot(face) {
  return FACE_KINDS[face] === "multi";
}

// Target render geometry is defined once in lib/targetGeometry.js and imported
// by BOTH this file and components/Target.jsx, so the pixel→radius layer below
// can never drift from what the SVG actually renders (issue #1368 / H6).

// Spot centres by multi-spot face id, in viewbox units. Mirrors _SPOT_CENTERS
// in backend/app/scoring.py — the two must stay in sync.
const SPOT_CENTERS_BY_FACE = {
  vegas: VEGAS_SPOT_CENTERS,
  "3spot-tri": TRI_SPOT_CENTERS,
  nfaa5spot: NFAA5_SPOT_CENTERS,
  wafield3spot: WAFIELD3_SPOT_CENTERS,
};

/**
 * The index of the spot nearest to (vbX, vbY) on a multi-spot face, or `null`
 * for a single-spot face. Coordinates are in viewbox units (0–240, centre
 * 120,120). Used to group arrows for the multi-spot lowest-arrow rule.
 */
export function spotForPoint(face, vbX, vbY) {
  const centers = SPOT_CENTERS_BY_FACE[face];
  if (!centers) return null;
  let nearest = 0;
  let nearestDist = Infinity;
  for (let i = 0; i < centers.length; i++) {
    const d = Math.hypot(vbX - centers[i].x, vbY - centers[i].y);
    if (d < nearestDist) { nearestDist = d; nearest = i; }
  }
  return nearest;
}

/**
 * Centre-of-group marker positions in viewBox units (0–240, centre 120,120),
 * as an ARRAY of `{x,y}` points (empty `[]` when no arrows are placed, so
 * callers can `.map` with no null-guard). Shared by the Round Detail arrow
 * visualisations so the "ring + X" marker lands on the physical group centre.
 *
 * `placedArrows` is any array of arrow objects; only those with `x != null &&
 * y != null` are considered. Misses (score `0`/`null`/`"M"`) are INCLUDED on
 * single-spot faces — a miss still has a placed position that properly relates
 * to the single centre — but EXCLUDED on multi-spot faces, where a miss lands
 * in the blank paper between spots and can't be attributed to any one spot, so
 * folding it into a nearest spot's frame would distort the group centre.
 *
 * Single-spot face: a one-element array holding the plain mean of the
 * arrow positions (misses included).
 *
 * Multi-spot face: each arrow is folded into its assigned spot's local frame
 * using the SAME nearest-spot assignment as scoring (`spotForPoint`), the local
 * offsets `(lx,ly)` are averaged, then that SAME in-spot offset is replicated
 * on EVERY spot — returning one marker per spot centre. So 10/10/10 on a 3-spot
 * (one centred arrow per spot) folds to offset ≈ (0,0) and a marker lands ON
 * the 10 of EACH spot, not in the blank paper between spots. All spots on a
 * given face share radius/orientation, so folding in raw vb units (no per-spot
 * normalisation) is correct.
 *
 * This REPLICATED pooled offset is the PER-END surface (ArrowGrouping): an end
 * has too few arrows per spot for independent means to be stable, and the
 * pooled offset reads as "your aim this end". Whole-round surfaces show each
 * spot's own drift instead — see `spotIndependentCentersVb` (#1843).
 */
export function spotAwareCentersVb(face, placedArrows) {
  if (!Array.isArray(placedArrows)) return [];
  const pts = [];
  for (const a of placedArrows) {
    if (a == null || a.x == null || a.y == null) continue;
    pts.push({ x: toVbX(a.x), y: toVbY(a.y), score: a.score });
  }
  if (!pts.length) return [];

  const mean = () => ({
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
  });

  const centers = SPOT_CENTERS_BY_FACE[face];
  // Single-spot: plain mean of ALL placed arrows (misses included).
  if (!isMultiSpot(face) || !centers || !centers.length) return [mean()];

  // Multi-spot: a miss lands in the blank paper between spots and can't be
  // attributed to a spot, so exclude misses before folding. (A miss is score
  // 0/null/undefined/"M" — same predicate as scoreLabel.)
  const isMiss = (s) => s === 0 || s == null || s === "M";
  const hits = pts.filter((p) => !isMiss(p.score));
  if (!hits.length) return [];

  // Fold each non-miss arrow into its assigned spot's local frame, average the offsets.
  let lx = 0;
  let ly = 0;
  for (const p of hits) {
    const c = centers[spotForPoint(face, p.x, p.y)];
    lx += p.x - c.x;
    ly += p.y - c.y;
  }
  lx /= hits.length;
  ly /= hits.length;

  // Replicate the same in-spot offset on every spot centre — one marker per spot.
  return centers.map((c) => ({ x: c.x + lx, y: c.y + ly }));
}

/**
 * INDEPENDENT per-spot group-centre marker positions in viewBox units (0–240),
 * as an ARRAY of `{x,y}` points (`[]` when nothing is placed). The WHOLE-ROUND
 * counterpart to `spotAwareCentersVb` (#1843):
 *
 * Single-spot face: identical to `spotAwareCentersVb` — a one-element array
 * holding the plain mean of all placed arrows, misses included.
 *
 * Multi-spot face: same placed/miss filtering as `spotAwareCentersVb` (misses
 * — score 0/null/"M" — land in the blank paper between spots and are
 * excluded), each hit is assigned to its nearest spot (`spotForPoint`, the
 * same assignment scoring uses), and ONE marker is returned per spot that has
 * ≥ 1 hit, at the mean position of THAT spot's own hits. Spots with no hits
 * get NO marker — nothing is pooled or replicated.
 *
 * Split rationale: the per-end surface (ArrowGrouping) keeps the replicated
 * pooled offset — a single end rarely has enough arrows per spot for stable
 * independent means. Whole-round surfaces (ShotPatternOverlay, Stats
 * FaceThumb) have the full round's arrows, so each spot can honestly show its
 * own drift, mirroring the per-spot group ellipses (#1836).
 */
export function spotIndependentCentersVb(face, placedArrows) {
  if (!Array.isArray(placedArrows)) return [];
  const pts = [];
  for (const a of placedArrows) {
    if (a == null || a.x == null || a.y == null) continue;
    pts.push({ x: toVbX(a.x), y: toVbY(a.y), score: a.score });
  }
  if (!pts.length) return [];

  const mean = (arr) => ({
    x: arr.reduce((s, p) => s + p.x, 0) / arr.length,
    y: arr.reduce((s, p) => s + p.y, 0) / arr.length,
  });

  const centers = SPOT_CENTERS_BY_FACE[face];
  // Single-spot: plain mean of ALL placed arrows (misses included).
  if (!isMultiSpot(face) || !centers || !centers.length) return [mean(pts)];

  // Multi-spot: exclude misses (same predicate as spotAwareCentersVb), then
  // group each hit under its nearest spot.
  const isMiss = (s) => s === 0 || s == null || s === "M";
  const hits = pts.filter((p) => !isMiss(p.score));
  if (!hits.length) return [];

  const bySpot = new Map();
  for (const p of hits) {
    const spot = spotForPoint(face, p.x, p.y);
    if (!bySpot.has(spot)) bySpot.set(spot, []);
    bySpot.get(spot).push(p);
  }

  // One marker per hit-bearing spot, at that spot's own mean — ordered by
  // spot index so the output is deterministic.
  return [...bySpot.keys()].sort((a, b) => a - b).map((spot) => mean(bySpot.get(spot)));
}

/**
 * Public face-aware wrapper for the multi-spot lowest-arrow rule (#1472/#1477).
 *
 * On a multi-spot face, two or more arrows on the SAME spot are a bad grouping:
 * only the lowest-scoring counts and the rest become "M". This surfaces the
 * downgrade LIVE so the in-progress scorecard end matches what Confirm persists
 * and the review page shows. Single-spot faces are returned unchanged.
 *
 * `spots` is parallel to `arrows`, giving the spot index each arrow landed on;
 * a `null`/absent spot is left ungrouped — keypad arrows (no real tap position)
 * pass `null` and are never grouped or downgraded.
 *
 * The lowest comparison values an "X" at `scoringZones` (the face max), NOT a
 * hardcoded 10 — on a 5-/6-zone multi-spot face (nfaa5spot=5, wafield3spot=6)
 * an X must outscore the top digit, else the live path would keep a different
 * arrow than the server (the #1472 seam bug). On a tie the FIRST index is kept.
 * Mirrors backend `apply_multi_spot_rule` in `backend/app/scoring.py`.
 *
 * Returns a new effective per-arrow score list (input is not mutated).
 */
export function applyMultiSpotRule(face, arrows, spots, scoringZones = 10) {
  if (!isMultiSpot(face) || !spots) return arrows.slice();
  return _applyMultiSpotRule(arrows, spots, scoringZones);
}

export function hitZone(face, vbX, vbY) {
  switch (face) {
    case "vegas":
      return _hitVegas(vbX, vbY);
    case "3spot-tri":
      return _hit3SpotTri(vbX, vbY);
    case "nfaa5spot":
      return _hitNFAA5Spot(vbX, vbY);
    case "nfaaspot":
      return _hitNFAASingleSpot(vbX, vbY);
    case "nfaafield":
      return _hitNFAAField(vbX, vbY);
    case "3d":
      return _hit3D(vbX, vbY);
    case "ibo3d":
      return _hitIBO3D(vbX, vbY);
    case "3d-2arrow":
      return _hit3D2Arrow(vbX, vbY);
    case "imperial5zone":
      return _hitImperial5Zone(vbX, vbY);
    case "wa5zone":
      return _hitWA5Zone(vbX, vbY);
    case "wa6ring":
      return _hitWA6Ring(vbX, vbY);
    case "wa5ring80":
    case "wa5ring122":
      return _hitWA5Ring(vbX, vbY);
    case "wafield40":
    case "wafield60":
    case "wafield80":
      return _hitWAField(vbX, vbY);
    case "wafield3spot":
      return _hitWAField3Spot(vbX, vbY);
    case "worcester":
      return _hitWorcester(vbX, vbY);
    case "nfaahunter":
      return _hitNFAAHunter(vbX, vbY);
    default:
      // "wa40", "wa122", and any unknown face use WA 10-ring
      return _hitWA(vbX, vbY);
  }
}

// Every _hit* helper below resolves its ring geometry (centre/divisor) as
// before, then dispatches the resulting normalised radius through the
// table-driven zoneForRadiusFromTable(face, r) (#1680 PR3) instead of a
// per-face if/else chain. Several helpers pass a fixed representative face
// code rather than the caller's `face` argument — e.g. _hitWA always resolves
// via "wa40" — because hitZone's outer switch (below) already groups every
// face sharing that helper's exact radius_map onto it (wa40/wa122/wa60/wa80/
// nfaaoutdoor/unknown all share the wa_full table; vegas/3spot-tri share
// wa_triple; …), so the specific code passed here only has to resolve to the
// right faceType, not identify the caller's actual face.

function _hitWA(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R; // outer ring SVG radius (20-unit miss margin to the viewBox edge)
  return r > 1.0 ? "M" : zoneForRadiusFromTable("wa40", r);
}

function _hitVegas(vbX, vbY) {
  let nearestDist = Infinity;
  for (const c of VEGAS_SPOT_CENTERS) {
    const d = Math.hypot(vbX - c.x, vbY - c.y);
    if (d < nearestDist) nearestDist = d;
  }
  const r = nearestDist / VEGAS_SPOT_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("vegas", r);
}

function _hit3SpotTri(vbX, vbY) {
  let nearestDist = Infinity;
  for (const c of TRI_SPOT_CENTERS) {
    const d = Math.hypot(vbX - c.x, vbY - c.y);
    if (d < nearestDist) nearestDist = d;
  }
  const r = nearestDist / TRI_SPOT_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("3spot-tri", r);
}

function _hitNFAA5Spot(vbX, vbY) {
  let nearestDist = Infinity;
  for (const c of NFAA5_SPOT_CENTERS) {
    const d = Math.hypot(vbX - c.x, vbY - c.y);
    if (d < nearestDist) nearestDist = d;
  }
  const r = nearestDist / NFAA5_SPOT_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("nfaa5spot", r);
}

function _hitNFAASingleSpot(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / NFAA_SINGLE_OUTER_R; // outer ring radius in viewbox units (reference: 201.8 × 0.48)
  return r > 1.0 ? "M" : zoneForRadiusFromTable("nfaaspot", r);
}

function _hitNFAAField(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R; // outer ring SVG radius for field face (miss margin to the viewBox edge)
  return r > 1.0 ? "M" : zoneForRadiusFromTable("nfaafield", r);
}

function _hit3D(vbX, vbY) {
  const dist = Math.hypot(vbX - D3_VITAL.x, vbY - D3_VITAL.y);
  const r = dist / D3_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("3d", r);
}

function _hitIBO3D(vbX, vbY) {
  const dist = Math.hypot(vbX - D3_VITAL.x, vbY - D3_VITAL.y);
  const r = dist / D3_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("ibo3d", r);
}

function _hit3D2Arrow(vbX, vbY) {
  const dist = Math.hypot(vbX - D3_VITAL.x, vbY - D3_VITAL.y);
  const r = dist / D3_OUTER_R;
  return zoneForRadiusFromTable("3d-2arrow", r);
}

function _hitImperial5Zone(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("imperial5zone", r);
}

// WA 5-zone face (gold 9 / red 7 / blue 5 / black 3 / white 1) — same 0.20-wide
// band geometry as the imperial 5-zone face (identical radius_map values), but
// resolved through its OWN face code so the table lookup stays face-accurate.
function _hitWA5Zone(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("wa5zone", r);
}

function _hitWA6Ring(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("wa6ring", r);
}

function _hitWA5Ring(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("wa5ring80", r);
}

function _hitWorcester(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("worcester", r);
}

function _hitNFAAHunter(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("nfaahunter", r);
}

function _hitWAField(vbX, vbY) {
  const dist = Math.hypot(vbX - CENTER.x, vbY - CENTER.y);
  const r = dist / WA_OUTER_R;
  return r > 1.0 ? "M" : zoneForRadiusFromTable("wafield40", r);
}

// WA Field 3-spot: find the nearest of the 3 spots and score that spot.
// Centres + per-spot radius come from lib/targetGeometry.js — the single
// declaration shared with Target.jsx and mirrored by the backend (#1667).
function _hitWAField3Spot(vbX, vbY) {
  let nearest = null;
  let nearestDist = Infinity;
  for (const c of WAFIELD3_SPOT_CENTERS) {
    const d = Math.hypot(vbX - c.x, vbY - c.y);
    if (d < nearestDist) { nearestDist = d; nearest = c; }
  }
  if (!nearest || nearestDist > WAFIELD3_SPOT_R) return "M";
  const r = nearestDist / WAFIELD3_SPOT_R;
  return String(zoneForRadiusFromTable("wafield3spot", r));
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoring engine — converts arrows into end totals according to round rules.
//
// This is the frontend half of the shared scoring engine; the backend half is
// backend/app/scoring.py. Both are verified against shared/scoring-fixtures.json
// so the two implementations cannot drift apart.
// ─────────────────────────────────────────────────────────────────────────────

// Legal score strings by zone count — the fallback for a face that does not
// declare its own set (see `faceOverridesLegalScores`). The 10-zone run is the
// shared JSON's plain run (`_DEFAULT_LEGAL_SCORES`, wa_full) rather than a
// second typing of it; the 5-zone run has no JSON layout to read it off, since
// it is a scoring_zones property of the round type, not of a face.
const LEGAL_SCORES = {
  10: _DEFAULT_LEGAL_SCORES,
  5:  new Set(["X", "5", "4", "3", "2", "1", "M"]),
};

/**
 * The scoring zone an arrow at `radius` lands in on `face`. Boundary radii
 * resolve to the inner ring — the line-cutter rule (an arrow touching a line
 * scores the higher value). An unknown face scores "M". Table-driven
 * (shared/face-definitions.json) — resolves any face in the shared registry,
 * including wa60/wa80/nfaaoutdoor (round-type-only faces excluded from
 * SUPPORTED_FACES above, but valid here exactly as the old per-face switch
 * recognised them). With `innerTen`, a resolved outer-10 zone downgrades to
 * "9" (the compound rule, #1868) — an X stays X.
 */
export function zoneForRadius(face, radius, innerTen = false) {
  return applyInnerTen(zoneForRadiusFromTable(face, radius) ?? "M", innerTen, face);
}

/** Read a round-type field whether it is a model object (snake_case) or one
 *  of the camelCase dicts from the shared fixture. */
function rtField(rt, snake, camel, dflt) {
  if (rt == null) return dflt;
  if (camel in rt) return rt[camel];
  if (snake in rt) return rt[snake];
  return dflt;
}

/** Point value of a score: an X is worth the max ring value, a miss 0, a
 *  digit itself. Illegal strings count as 0. */
function zoneValue(s, scoringZones) {
  if (s === "X") return scoringZones;
  if (s === "M") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Multi-spot rule. On a multi-spot face, two or more arrows on the *same*
 * spot indicate a bad/illegal grouping: only the lowest-scoring of them
 * counts and the rest are downgraded to a miss. `spots` is a list parallel
 * to `arrows` giving the spot each arrow landed on; a `null`/absent spot is
 * left ungrouped. Returns the effective score list.
 */
function _applyMultiSpotRule(arrows, spots, scoringZones) {
  const effective = arrows.slice();
  const groups = new Map();
  for (let i = 0; i < arrows.length; i++) {
    const s = spots[i];
    // A miss is off every spot's scoring area, so it is not part of any spot's
    // grouping — it must never become the kept-lowest nor downgrade real hits
    // (a dragged-off arrow is "M" but still nearest some spot). (#1483)
    if (s == null || arrows[i] === "M") continue;
    if (!groups.has(s)) groups.set(s, []);
    groups.get(s).push(i);
  }
  for (const idxs of groups.values()) {
    if (idxs.length < 2) continue;
    let keep = idxs[0];
    for (const i of idxs) {
      if (zoneValue(arrows[i], scoringZones) < zoneValue(arrows[keep], scoringZones)) {
        keep = i;
      }
    }
    for (const i of idxs) {
      if (i !== keep) effective[i] = "M";
    }
  }
  return effective;
}

/**
 * Score one end of `arrows` against `roundType`.
 *
 * `spots`, when given, is a list parallel to `arrows` naming the spot each
 * arrow landed on; the multi-spot rule (see `_applyMultiSpotRule`) then
 * downgrades all but the lowest arrow on any shared spot.
 *
 * Returns { endTotal, runningTotal, xCount, valid, violations }, where
 * `runningTotal` is `priorTotal + endTotal` and `valid` is true exactly when
 * `violations` is empty. Validity is judged on the arrows as entered; only
 * the totals reflect the multi-spot downgrade. Violations are reported in a
 * stable order: the arrow-count check first, then each illegal score in
 * arrow order.
 */
export function score(roundType, arrows, priorTotal = 0, spots = null) {
  const arrowsPerEnd = rtField(roundType, "arrows_per_end", "arrowsPerEnd", null);
  const scoringZones = rtField(roundType, "scoring_zones", "scoringZones", 10);
  const xDefinition  = rtField(roundType, "x_definition", "xDefinition", "inner10");
  const innerTen     = rtField(roundType, "inner_ten", "innerTen", false);

  const violations = [];
  if (arrowsPerEnd != null && arrows.length !== arrowsPerEnd) {
    violations.push(`expected ${arrowsPerEnd} arrows, got ${arrows.length}`);
  }
  const face = rtField(roundType, "face", "face", null);
  // A face whose layout declares something other than the plain ten-zone run
  // (3D, NFAA, WA Field, the truncated WA rings…) validates against its own
  // `legal_scores` from the shared JSON; everything else falls back to the run
  // for the round type's scoring_zones (#2041).
  const special = faceOverridesLegalScores(face);
  const legal = special
    ? legalScoresForFace(face)
    : LEGAL_SCORES[scoringZones] || LEGAL_SCORES[10];
  const legalLabel = special ? `the ${face} face` : `a ${scoringZones}-zone face`;
  for (const a of arrows) {
    if (!legal.has(a)) {
      violations.push(`illegal score '${a}' for ${legalLabel}`);
    }
  }
  // inner-ten (compound): a raw "10" is invalid — only X scores 10 (#1868).
  // Face-conditional: inert on a face with no X/10/9 trio (#2172).
  if (innerTen) {
    violations.push(...innerTenViolations(arrows, face));
  }

  const effective = spots
    ? _applyMultiSpotRule(arrows, spots, scoringZones)
    : arrows;

  const endTotal = effective.reduce((t, a) => t + zoneValue(a, scoringZones), 0);
  // inner10: only an X is an X. ten_only (compound): any 10 counts too.
  const xCount = xDefinition === "ten_only"
    ? effective.filter((a) => a === "X" || a === "10").length
    : effective.filter((a) => a === "X").length;

  return {
    endTotal,
    runningTotal: priorTotal + endTotal,
    xCount,
    valid: violations.length === 0,
    violations,
  };
}
